import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Trash2, FolderKanban } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { useBoundStore } from '../../store';
import { useI18n } from '../../i18n';

interface ChatHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const ChatHistoryDrawer: React.FC<ChatHistoryDrawerProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const qagentChatsRaw = useBoundStore((state) => state.qagentChats);
  const deleteQAgentChat = useBoundStore((state) => state.deleteQAgentChat);
  const isQAgentLoading = useBoundStore((state) => state.isQAgentLoading);

  // Ensure qagentChats is always an array
  const qagentChats = Array.isArray(qagentChatsRaw) ? qagentChatsRaw : [];

  const handleChatClick = (chatId: string) => {
    navigate(`/qagent/${chatId}`);
    onClose();
  };

  const handleDelete = async (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    await deleteQAgentChat(chatId);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent side="right" className="w-80 sm:w-96">
        <SheetHeader>
          <SheetTitle>{t.qagentHome.chatHistory.title}</SheetTitle>
          <SheetDescription>
            {t.qagentHome.chatHistory.description}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-10rem)] mt-4 -mx-6 px-6">
          {isQAgentLoading ? (
            <div className="text-center text-muted-foreground py-8">
              {t.qagentHome.chatHistory.loading}
            </div>
          ) : qagentChats.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground text-sm">
                {t.qagentHome.chatHistory.noConversations}
              </p>
              <p className="text-muted-foreground/60 text-xs mt-1">
                {t.qagentHome.chatHistory.startFromHome}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {qagentChats.map((chat) => (
                <button
                  key={chat.id}
                  onClick={() => handleChatClick(chat.id)}
                  className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent transition-colors group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {chat.title || t.qagentHome.chatHistory.untitledChat}
                      </p>
                      {chat.lastMessage && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {chat.lastMessage}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground/60">
                          {t.qagentHome.chatHistory.messageCount(chat.messageCount)}
                        </span>
                        {chat.projectName && (
                          <span className="inline-flex items-center gap-1 text-xs text-primary/70">
                            <FolderKanban className="h-3 w-3" />
                            {chat.projectName}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 shrink-0"
                      onClick={(e) => handleDelete(e, chat.id)}
                      aria-label={t.qagentHome.chatHistory.deleteChat}
                      title={t.qagentHome.chatHistory.deleteChat}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default ChatHistoryDrawer;
